import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { applyThemePalettes } from '@/lib/siteTheme';
import {
  HeroSection,
  TextSection,
  CardsSection,
  CTASection,
  FAQSection,
  FeaturesSection,
  ImageSection,
  GallerySection,
  CarouselSection,
  TestimonialsSection,
  SpacerSection,
  CustomHTMLSection,
  ContactFormSection
} from './SectionRenderers';
import { EventsListSection, GamesListSection } from './DynamicSections';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fixMojibake(value) {
  if (typeof value === "string") {
    if (!/[ÃÂ]/.test(value) && !/\\u00/i.test(value)) return value;

    try {
      if (/\\u00/i.test(value)) {
        return value.replace(/\\u([\dA-Fa-f]{4})/g, (_, code) =>
          String.fromCharCode(parseInt(code, 16))
        );
      }

      return decodeURIComponent(escape(value));
    } catch {
      return value
        .replace(/\\u([\dA-Fa-f]{4})/g, (_, code) => String.fromCharCode(parseInt(code, 16)))
        .replace(/Ã‚/g, "");
    }
  }

  if (Array.isArray(value)) {
    return value.map(fixMojibake);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, fixMojibake(val)]));
  }

  return value;
}

// Section type to component mapping
const SECTION_COMPONENTS = {
  hero: HeroSection,
  text: TextSection,
  cards: CardsSection,
  cta: CTASection,
  faq: FAQSection,
  features: FeaturesSection,
  image: ImageSection,
  gallery: GallerySection,
  carousel: CarouselSection,
  testimonials: TestimonialsSection,
  spacer: SpacerSection,
  custom_html: CustomHTMLSection,
  contact_form: ContactFormSection,
  events_list: EventsListSection,
  games_list: GamesListSection
};

// Dynamic Page Renderer
export default function DynamicPage({ slug, fallbackContent }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === 'light';

  useEffect(() => {
    if (!loading && page && location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const offset = el.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: offset, behavior: 'smooth' });
        }
      }, 300);
    }
  }, [loading, page, location.hash]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/pages/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setPage(fixMojibake(data));
        } else {
          setError('Page not found');
        }
      } catch (err) {
        console.error('Error fetching page:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#fccf1f]"></div>
      </div>
    );
  }

  // If no CMS content or error, render fallback content
  if (error || !page || page.sections.length === 0) {
    return fallbackContent || null;
  }

  // Filter visible sections and sort by order
  const visibleSections = page.sections
    .filter(section => section.visible !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className={`min-h-screen ${isLight ? 'bg-background' : 'bg-[#050505]'}`}>
      {visibleSections.map((section) => (
        <SectionRenderer key={section.id} section={section} pageSlug={slug} />
      ))}
    </div>
  );
}

// Individual Section Renderer
export function SectionRenderer({ section, pageSlug }) {
  const Component = SECTION_COMPONENTS[section.type];
  
  if (!Component) {
    console.warn(`Unknown section type: ${section.type}`);
    return null;
  }
  
  return (
    <Component
      content={section.content || {}}
      style={section.style || {}}
      pageSlug={pageSlug}
    />
  );
}

// Hook to fetch page data
export function useCMSPage(slug) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/pages/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setPage(fixMojibake(data));
        } else {
          setError('Page not found');
        }
      } catch (err) {
        console.error('Error fetching page:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  return { page, loading, error };
}

// Hook to fetch site settings
export function useSiteSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/settings`);
        if (res.ok) {
          const data = await res.json();
          setSettings(data);
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  return { settings, loading };
}

// Component to apply site-wide settings
export function SiteSettingsProvider({ children }) {
  const { settings, loading } = useSiteSettings();

  useEffect(() => {
    if (settings && !loading) {
      // Apply custom CSS
      if (settings.customCss) {
        const styleEl = document.createElement('style');
        styleEl.id = 'cms-custom-css';
        styleEl.textContent = settings.customCss;
        
        // Remove existing custom CSS
        const existing = document.getElementById('cms-custom-css');
        if (existing) existing.remove();
        
        document.head.appendChild(styleEl);
      }

      // Apply CSS variables for colors
      if (settings.colors) {
        applyThemePalettes(settings.colors);
      }

      // Apply custom JS (be careful with this!)
      if (settings.customJs) {
        try {
          // Execute custom JS in a safe way
          const scriptEl = document.createElement('script');
          scriptEl.id = 'cms-custom-js';
          scriptEl.textContent = settings.customJs;
          
          // Remove existing custom JS
          const existing = document.getElementById('cms-custom-js');
          if (existing) existing.remove();
          
          document.body.appendChild(scriptEl);
        } catch (err) {
          console.error('Error executing custom JS:', err);
        }
      }
    }
  }, [settings, loading]);

  return <>{children}</>;
}
